import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Calendar, ArrowRight, Loader2, AlertCircle, Inbox } from 'lucide-react';
import api from '../../services/api';
import { AddToRMAModal } from '../../components/rma/AddToRMAModal';
import { RMAProductCard } from '../../components/rma/RMAProductCard';
import { useRMACart } from '../../context/RMACartContext';
import { fetchQuantitaRese } from '../../services/rmaService';
import { usePublicConfig } from '../../hooks/usePublicConfig';
import { productService } from '../../services/productService';
import { numeroDocumento } from '../../utils/numeroDocumento';
import { codiceOemDaMostrare } from '../../utils/oemCodes';

interface DocParams {
  tipork: string;
  anno: string;
  serie: string;
  numdoc: string;
}

/** Il documento che porta la merce di una riga: la riga stessa lo dice per
 *  una Fattura differita (il suo D.D.T.), altrimenti è il documento aperto. */
interface DocKey { tipork: string; anno: number; serie: string; numdoc: number }
const chiave = (k: DocKey) => `${k.tipork}-${k.anno}-${k.serie}-${k.numdoc}`;

export default function RMADocumentItemsPage() {
  const { tipork, anno, serie, numdoc } = useParams<Record<keyof DocParams, string>>();
  const navigate = useNavigate();
  const { items: rmaItems, totalUnits } = useRMACart();
  const { rma_return_window_months: returnWindowMonths } = usePublicConfig();
  const [modalProduct, setModalProduct] = useState<any>(null);
  const [doc, setDoc] = useState<any>(null);
  // Pezzi già resi, per documento che porta la merce e per SKU.
  const [quantitaRese, setQuantitaRese] = useState<Record<string, Record<string, number>>>({});
  const [loading, setLoading] = useState(true);
  // Le righe articolo vivono solo su NTS. Se il gestionale non risponde il
  // backend restituisce comunque la testata, ma senza righe: va detto, perché
  // "documento senza articoli" e "righe non leggibili" sono la stessa
  // schermata solo se il guasto resta invisibile.
  const [righeNonDisponibili, setRigheNonDisponibili] = useState(false);
  const [scartate, setScartate] = useState(0);

  useEffect(() => {
    if (!tipork || !anno || serie == null || !numdoc) {
      // Senza uscire da ``loading`` la pagina resterebbe su uno spinner
      // perpetuo invece di dire che il riferimento non è valido.
      setLoading(false);
      setDoc(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const aperto: DocKey = { tipork, anno: Number(anno), serie, numdoc: Number(numdoc) };
    api.get(`/api/clienti/me/documenti/${tipork}/${anno}/${encodeURIComponent(serie)}/${numdoc}`)
      .then(async (r) => {
        const d = r.data;
        const righe = (d.items || []).map((it: any) => ({ ...it, origine: it.documento ?? aperto }));
        const origini: DocKey[] = Array.from(new Map(righe.map((it: any) => [chiave(it.origine), it.origine])).values()) as DocKey[];
        const qtyRese: Record<string, Record<string, number>> = Object.fromEntries(await Promise.all(
          origini.map(async (o) => [chiave(o), await fetchQuantitaRese(o.tipork, o.anno, o.serie, o.numdoc).catch(() => ({}))]),
        ));
        // Esclude righe di servizio (spese/sconti con codice fittizio o prezzo/qtà nulli)
        const validItems = righe.filter((it: any) => (it.sku || '').trim().length > 1 && it.prezzoUnitario > 0 && it.quantita > 0);

        // Arricchimento marca/categoria/OEM in una sola chiamata invece di
        // una per riga: un documento da 20 righe faceva 20 richieste.
        const skus = Array.from(new Set(validItems.map((it: any) => it.sku))) as string[];
        let bySku: Record<string, any> = {};
        if (skus.length > 0) {
          try {
            const res = await productService.list({ skus: skus.join(','), limit: Math.min(skus.length, 200) });
            bySku = Object.fromEntries(res.items.map((p: any) => [p.sku, p]));
          } catch {
            // Arricchimento facoltativo: senza marca/categoria la riga resta
            // pienamente utilizzabile per il reso.
            bySku = {};
          }
        }
        const enriched = validItems.map((it: any) => {
          const p = bySku[it.sku];
          return p
            ? { ...it, brand: p.brand, category: p.category, dtkCodice: p.sku, codiceOem: codiceOemDaMostrare(p) || undefined }
            : { ...it, brand: '', category: 'altro' };
        });

        if (!cancelled) {
          setDoc({ ...d, items: enriched });
          setQuantitaRese(qtyRese);
          setRigheNonDisponibili(Boolean(d.righe_non_disponibili));
          setScartate(righe.length - validItems.length);
        }
      })
      .catch(() => { if (!cancelled) setDoc(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tipork, anno, serie, numdoc]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center text-muted-foreground" data-testid="rma-doc-items-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Caricamento documento...
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-semibold mb-2">Documento non trovato</h1>
        <Link to="/area-clienti/rma/da-documento" className="text-sm text-[hsl(214,58%,46%)] hover:underline">Torna alla lista</Link>
      </div>
    );
  }

  const dateFmt = doc.date ? new Date(doc.date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const handleAddProduct = (item: any, effectiveMaxQty: number) => {
    setModalProduct({
      sku: item.sku,
      title: item.title,
      brand: item.brand,
      category: item.category,
      dtkCodice: item.dtkCodice,
      codiceOem: item.codiceOem || item.sku,
      orderId: doc.numero,
      orderDate: doc.date,
      orderStatus: 'consegnato',
      docTipork: item.origine.tipork,
      docAnno: item.origine.anno,
      docSerie: item.origine.serie,
      docNumdoc: item.origine.numdoc,
      maxQty: effectiveMaxQty,
      qtyPurchased: item.quantita,
      unitPrice: item.prezzoUnitario,
    });
  };

  const inCartCount = (o: DocKey, sku: string): number => {
    const ci = rmaItems.find(i => i.docTipork === o.tipork && i.docAnno === o.anno && i.docSerie === o.serie && i.docNumdoc === o.numdoc && i.sku === sku);
    return ci?.units.length || 0;
  };
  const titolo = doc.tipoRk === 'B'
    ? (doc.fattura ? `Articoli Fattura ${numeroDocumento(doc.fattura.numero)}` : `Articoli D.D.T. ${numeroDocumento(doc.numero)}`)
    : `Articoli Fattura ${numeroDocumento(doc.numero)}`;

  return (
    <div data-testid="rma-doc-items-page">
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[hsl(214,58%,41%)]/10 flex items-center justify-center flex-shrink-0">
            <Package className="w-5 h-5 text-[hsl(214,58%,41%)]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {titolo}
            </h1>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {dateFmt}</span>
              <span>{doc.items.length} articoli</span>
              {doc.total !== null && doc.total !== undefined && (
                <span className="font-medium text-foreground">€ {doc.total.toFixed(2).replace('.', ',')}</span>
              )}
            </div>
          </div>
        </div>
        {totalUnits > 0 && (
          <button
            onClick={() => navigate('/area-clienti/rma/preparazione')}
            className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-xs font-medium flex items-center gap-1.5 hover:bg-emerald-700 transition-colors"
            data-testid="rma-go-to-cart"
          >
            Vai al carrello resi ({totalUnits})
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {righeNonDisponibili && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-start gap-2 mb-3" data-testid="rma-doc-items-erp-down">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Gli articoli di questo documento non sono leggibili in questo momento
            perché il gestionale non risponde. Riprova tra qualche minuto.
          </span>
        </div>
      )}

      {/* Items list */}
      <div className="space-y-2">
        {doc.items.length === 0 && !righeNonDisponibili && (
          <div className="rounded-xl border border-dashed p-10 text-center" data-testid="rma-doc-items-empty">
            <Inbox className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-semibold">Nessun articolo reso-abile in questo documento</p>
            <p className="text-xs text-muted-foreground mt-1">
              {scartate > 0
                ? 'Il documento contiene solo righe di servizio (spese, sconti o articoli a titolo gratuito).'
                : 'Il documento non riporta righe articolo.'}
            </p>
          </div>
        )}
        {doc.items.map((item: any) => {
          const alreadyRequested = (quantitaRese[chiave(item.origine)] || {})[item.sku] || 0;
          const inCart = inCartCount(item.origine, item.sku);

          return (
            <RMAProductCard
              // Uno stesso articolo può comparire su più righe del documento:
              // la sola sku non è una chiave univoca.
              key={`${chiave(item.origine)}-${item.riga}-${item.sku}`}
              sku={item.sku}
              title={item.title}
              brand={item.brand}
              quantity={item.quantita}
              alreadyRequested={alreadyRequested}
              unitPrice={item.prezzoUnitario}
              inCart={inCart}
              orderDate={doc.date}
              returnWindowMonths={returnWindowMonths}
              testId={`rma-doc-item-${item.sku}`}
              onAdd={(effectiveMaxQty) => handleAddProduct(item, effectiveMaxQty)}
            />
          );
        })}
      </div>

      <AddToRMAModal
        open={!!modalProduct}
        onClose={() => setModalProduct(null)}
        product={modalProduct}
      />
    </div>
  );
}
